import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useSiteLanguage } from "@/contexts/LanguageContext";
import { getPublicUiCopy } from "@/lib/publicPageLocalization";

const TOAST_COOLDOWN_MS = 2500;

function isEditableTarget(target) {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest(
      'input, textarea, select, [contenteditable="true"], [role="textbox"], [data-allow-copy], [data-allow-paste], .allow-copy, .allow-paste'
    )
  );
}

export default function ContentProtection() {
  const { language } = useSiteLanguage();
  const toastRef = useRef(0);
  const ui = getPublicUiCopy(language, "contentProtection");

  useEffect(() => {
    document.body.classList.add("rsi-public-content-protected");
    return () => document.body.classList.remove("rsi-public-content-protected");
  }, []);

  useEffect(() => {
    const showBlockedToast = (message) => {
      const now = Date.now();
      if (now - toastRef.current < TOAST_COOLDOWN_MS) return;
      toastRef.current = now;
      toast.error(message);
    };

    const handleContextMenu = (event) => {
      if (isEditableTarget(event.target)) return;
      event.preventDefault();
      showBlockedToast(ui.active);
    };

    const handleSelectStart = (event) => {
      if (isEditableTarget(event.target)) return;
      event.preventDefault();
    };

    const handleCopyLike = (event) => {
      if (isEditableTarget(event.target)) return;
      event.preventDefault();
      showBlockedToast(ui.copyRestricted);
    };

    const handleDragStart = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest("img, video, canvas, picture, svg")) {
        event.preventDefault();
        showBlockedToast(ui.mediaBlocked);
      }
    };

    const handleKeyDown = (event) => {
      const editable = isEditableTarget(event.target);
      const key = String(event.key || "").toLowerCase();
      const usesMeta = event.ctrlKey || event.metaKey;
      const blockedCombo =
        usesMeta && ["c", "x", "s", "u", "p"].includes(key) && (!editable || ["s", "u", "p"].includes(key));
      const blockedScreenKey =
        key === "printscreen" || (event.metaKey && event.shiftKey && ["3", "4", "5"].includes(key));

      if (blockedCombo) {
        event.preventDefault();
        showBlockedToast(ui.exportBlocked);
        return;
      }

      if (blockedScreenKey) {
        event.preventDefault();
        showBlockedToast(ui.screenshotRestricted);
      }
    };

    window.addEventListener("contextmenu", handleContextMenu, true);
    window.addEventListener("selectstart", handleSelectStart, true);
    window.addEventListener("copy", handleCopyLike, true);
    window.addEventListener("cut", handleCopyLike, true);
    window.addEventListener("paste", handleCopyLike, true);
    window.addEventListener("dragstart", handleDragStart, true);
    window.addEventListener("keydown", handleKeyDown, true);

    return () => {
      window.removeEventListener("contextmenu", handleContextMenu, true);
      window.removeEventListener("selectstart", handleSelectStart, true);
      window.removeEventListener("copy", handleCopyLike, true);
      window.removeEventListener("cut", handleCopyLike, true);
      window.removeEventListener("paste", handleCopyLike, true);
      window.removeEventListener("dragstart", handleDragStart, true);
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [ui]);

  return null;
}
